// @flow

import Request from '@insolus/request';
import type { AllPaginateParams, PaginatedResult, Translations } from 'stores/api/_types';
import type { RequestPromise } from '@insolus/request';

export type AdState =
    'drafted' |
    'depicted' |
    'previewed' |
    'published' |
    'expired' |
    'disabled' |
    'banned';

export type AdMediaItem = {|
    id: number,
    paths: {
        original: string,
        large: string,
        medium: string,
        thumb: string
    }
|};

type AdBase = {|
    priority: number | null,
    streetAddress: string,
    modelName: string,
    phoneArea: string,
    phoneNumber: string,
    currency: string,
    price: number | null,
    priceOnDemand: boolean,
    isRental: boolean,
    constructionYear: number,
    weight: number,
    operationHour: number,
    description: string,
    isFeatured: boolean,
    expireDate: string | null
|};

export type Ad = {|
    ...AdBase,
    id: number,
    state: AdState,
    manufacturer: {|
        id: number,
        name: string
    |},
    category: {|
        id: number,
        name: string
    |},
    adCondition: {|
        id: number,
        name: string
    |},
    country: {|
        id: string,
        name: string
    |},
    locality: {|
        id: number,
        fullName: string,
        name: string
    |},
    advertiser: {|
        id: number,
        email: string
    |},
    descriptionTranslation: Translations,
    media: AdMediaItem[],
    priorityExpireDate: string,
    featuredExpireDate: string,
    hasOrder?: boolean
|};

export type AdData = {|
    ...AdBase,
    manufacturer: {|
        id: number
    |},
    category: {|
        id: number
    |},
    adCondition: {|
        id: number
    |},
    advertiser: {|
        id: number
    |},
    country: {|
        id: string
    |},
    locality: {|
        id: number
    |},
    description: Translations,
    priorityExpireDate: string | null,
    featuredExpireDate: string | null
|};

export type PublishData = {|
    priority: number | null,
    modelName: string,
    streetAddress: string,
    phoneArea: string,
    phoneNumber: string,
    currency: string,
    price: number | null,
    priceOnDemand: boolean,
    isRental: boolean,
    constructionYear: number,
    weight: number,
    operationHour: number,
    manufacturer: {|
        id: number
    |},
    category: {|
        id: number
    |},
    adCondition: {|
        id: number
    |},
    country: {|
        id: string
    |},
    locality: {|
        id: number
    |},
    description: Translations
|};

export type AdOptions = {|
    isFeatured: boolean,
    priority: number | null
|};

export type AdPriceData = {|
    currency: string,
    price: number | null,
    priceOnDemand: boolean,
    isRental: boolean,
    operationHour: number | null
|};

// ------------------------------------------------------
// -
// -    Functions
// -
// ------------------------------------------------------

const all = (
    params?: AllPaginateParams
): RequestPromise<PaginatedResult<Ad[]>> => Request.get('/machines', { params });

const get = (id: number): RequestPromise<Ad> => Request.get(`/machines/${id}`);

const add = (ad: AdData): RequestPromise<Ad> => Request.post('/machines', ad);

const edit = (id: number, data: AdData): RequestPromise<Ad> => Request.put(`/machines/${id}`, data);

const remove = (id: number): RequestPromise<void> => Request.delete(`/machines/${id}`);

const draft = (data: PublishData): RequestPromise<Ad> => Request.post('/machines/draft', data);

const editDraft = (
    id: number, data: PublishData
): RequestPromise<Ad> => Request.put(`/machines/draft/${id}`, data);

const depict = (id: number): RequestPromise<Ad> => Request.put(`/machines/depict/${id}`);

const setOptions = (id: number, options: AdOptions): RequestPromise<Ad> => (
    Request.put(`/machines/options/${id}`, options)
);

const setPrice = (id: number, data: AdPriceData): RequestPromise<Ad> => (
    Request.put(`/machines/price/${id}`, data)
);

const approve = (id: number): RequestPromise<Ad> => Request.put(`/machines/approve/${id}`);

const disable = (id: number): RequestPromise<Ad> => Request.put(`/machines/disable/${id}`);

const enable = (id: number): RequestPromise<Ad> => Request.put(`/machines/enable/${id}`);

const ban = (id: number): RequestPromise<Ad> => Request.put(`/machines/ban/${id}`);

const unban = (id: number): RequestPromise<Ad> => Request.put(`/machines/unban/${id}`);

const extend = (id: number): RequestPromise<Ad> => Request.put(`/machines/extend/${id}`);

const featured = (): RequestPromise<Ad[]> => Request.get('/machines/featured');

const addMessage = (
    adId: number, data: any
): RequestPromise<void> => Request.post(`/machines/${adId}/message`, data);

export default {
    all,
    get,
    add,
    edit,
    remove,
    draft,
    editDraft,
    depict,
    approve,
    disable,
    enable,
    featured,
    addMessage,
    setOptions,
    setPrice,
    ban,
    unban,
    extend
};
